
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'

import type {
    ClusterFeatureEntity,
    DispatcherWorkplaceCreateWaitListPayload,
    DispatcherWorkplaceDelivery,
    DispatcherWorkplaceTake,
    DispatcherWorkplaceWaitListStatus,
    DispatcherWorkplaceWaitListComment
} from '@/modules/DispatcherWorkplaceModule/types'

import type { PropType } from 'vue'

import { defineComponent, reactive, computed, onBeforeMount } from 'vue'

import DwPopup from '@/modules/DispatcherWorkplaceModule/ui/DwPopup/index.vue'
import DwButton from '@/modules/DispatcherWorkplaceModule/ui/DwButton/index.vue'

import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

import { dispatcherWorkplaceAPI } from '@/modules/DispatcherWorkplaceModule/services'

import useNotifications from '@/common/composable/useNotifications'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

const STATUSES_CODES_WITH_DATE = [319, 334, 336, 337, 338, 301, 307, 310, 304, 316, 345]

export default defineComponent({
    components: {
        DwPopup,
        DwButton,
        TheInput,
        TheSelect
    },
    props: {
        feature: {
            type: [Object, null] as PropType<ClusterFeature<ClusterFeatureEntity> | null>,
            default: null
        }
    },
    emits: ['close', 'added-to-wait-list'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            statusCode: null as number | null,
            newDate: null as string | null,
            is_other: false,
            comment: null as string | null,
            comment_other: null as string | null,
        })

        const validation$ = useVuelidate(
            {
                statusCode: { required },
                newDate: { required: requiredIf(() => isDate.value) },
            },
            state
        )

        const statuses_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as DispatcherWorkplaceWaitListStatus[],
        })

        const loadWaitListStatuses = async () => {
            if (!props.feature || !props.feature.entity) return

            try {
                statuses_state.is_loading = true

                const response = await dispatcherWorkplaceAPI.loadWaitListStatuses({
                    isDispatcher: 1,
                    type: props.feature.entity.type
                })

                statuses_state.list = response.data.data

                statuses_state.is_failed = false
            } catch (error) {
                console.error(error)

                statuses_state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении списка статусов произошла ошибка',
                })
            } finally {
                statuses_state.is_loading = false
            }
        }

        const comments_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as DispatcherWorkplaceWaitListComment[],
        })

        const loadWaitListComments = async () => {
            if (!props.feature || !props.feature.entity) return

            try {
                comments_state.is_loading = true

                const response = await dispatcherWorkplaceAPI.loadWaitListComments({
                    typeId: props.feature.entity.type
                })

                comments_state.list = response.data.data
                comments_state.list.push({ text: 'Другое' })

                comments_state.is_failed = false
            } catch (error) {
                console.error(error)

                comments_state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении списка комментариев произошла ошибка',
                })
            } finally {
                comments_state.is_loading = false
            }
        }

        const onClose = () => {
            if (state.is_loading) return
            emit('close')
        }

        const onSubmit = async () => {
            if (!props.feature || !props.feature.entity || state.is_loading) return

            try {
                state.is_loading = true

                const payload: DispatcherWorkplaceCreateWaitListPayload = {
                    code: state.statusCode as number,
                    stateId: 48,
                    value: state.newDate as string,
                }

                if (props.feature.entity.type === 1) {
                    payload.orderId = (props.feature.entity as DispatcherWorkplaceTake).orderId
                }

                if (props.feature.entity.type === 2) {
                    payload.invoiceId = (props.feature.entity as DispatcherWorkplaceDelivery).invoiceId
                }

                const comment = state.is_other ? state.comment_other : state.comment
                if (comment) payload.comment = comment

                await dispatcherWorkplaceAPI.createWaitList(payload)
                notifications.notify({ type: 'success', title: 'Добавлено в лист ожидания' })

                emit('added-to-wait-list', props.feature)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При добавлении в лист ожидания произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const onStatusChange = () => {
            if (!isDate.value) state.newDate = null
        }

        const onCommentChange = (value: string) => {
            state.comment_other = null

            if (value === 'Другое') state.is_other = true
            else state.is_other = false
        }

        onBeforeMount(() => {
            loadWaitListStatuses()
            loadWaitListComments()
        })

        const isDate = computed(() => Boolean(state.statusCode) && STATUSES_CODES_WITH_DATE.includes(state.statusCode as number))

        return {
            state,
            validation$,
            statuses_state,
            loadWaitListStatuses,
            comments_state,
            loadWaitListComments,
            onClose,
            onSubmit,
            onStatusChange,
            onCommentChange,
            isDate,
        }
    }
})
